import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  useMediaQuery,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { loadProfitsAndLosses } from "redux/profitsAndLosses/profitsAndLossesFunctions";
import DateRangeSelector from "components/DateRangeSelector";
import { RootState } from "redux/rootReducer";
import * as ProfitsAndLossesTypes from "../types/ProfitsAndLosses";
import { dateToString } from "utility/date";
import {
  asEur,
  asPercent,
  countPercent,
  seperateThousands,
} from "utility/numbers";
import { Cell } from "react-table";
import NoTransactions from "components/NoTransactions";
import { generateFilename } from "../utility/files";
import PortfolioName from "../components/PortfolioName";
import ExportExcel from "../components/ExportExcel";
import Table from "../components/Table";

const ProfitsAndLosses = () => {
  const [t] = useTranslation();

  const dispatch = useDispatch();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );

  const profitsAndLossesData = useSelector(
    (state: RootState) => state.profitsAndLosses.data ?? []
  );

  const profitsAndLossesLoading = useSelector(
    (state: RootState) => state.profitsAndLosses.loading
  );

  const startDate = useSelector((state: RootState) => state.user.startDate);

  const endDate = useSelector((state: RootState) => state.user.reportDate);

  React.useEffect(() => {
    if (reportControlId && startDate && endDate) {
      dispatch(loadProfitsAndLosses(reportControlId, startDate, endDate));
    }
  }, [dispatch, reportControlId, startDate, endDate]);

  return (
    <Grid
      container
      justifyContent={matchesMediaQuery ? "center" : "flex-start"}
    >
      <Grid item container md={10} sm={12}>
        {matchesMediaQuery ? (
          <Grid item xs={9}>
            <h1 style={{ color: "#003366" }}>{t("profits_and_losses")}</h1>
          </Grid>
        ) : (
          <></>
        )}
        <Grid
          container
          item
          md={3}
          sm={12}
          justifySelf="flex-start"
          alignContent="center"
        >
          <DateRangeSelector />
        </Grid>
      </Grid>
      <Grid item container md={10} style={{ padding: "1em 0" }}>
        {profitsAndLossesData.length > 0 ? (
          profitsAndLossesData.map(
            (
              data: ProfitsAndLossesTypes.PortfolioProfitsAndLosses,
              portfolioIndex: number
            ) => {
              const isExpandedDefault = portfolioIndex === 0 ? true : false;
              const fifoOpenCostSum = data.ProfitsAndLosses.reduce(
                (prev, curr) => {
                  return prev + curr.FifoOpenCost;
                },
                0
              );

              const dirtyAmountSum = data.ProfitsAndLosses.reduce(
                (prev, curr) => {
                  return prev + curr.DirtyAmount;
                },
                0
              );

              const profitLossSum = data.ProfitsAndLosses.reduce(
                (prev, curr) => {
                  return prev + curr.ProfitLoss;
                },
                0
              );

              const percentSum = countPercent(profitLossSum, fifoOpenCostSum);

              const tableColumns = [
                {
                  Header: t("instrument_name"),
                  accessor: "InstrumentName",
                  paddingRight: "10px",
                  width: "fit-content",
                },
                {
                  Header: t("transaction_date"),
                  accessor: "TransactionDate",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("clearing_date"),
                  accessor: "ClearingDate",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("count"),
                  Cell: (
                    cell: Cell<ProfitsAndLossesTypes.ProfitsAndLosses, number>
                  ) => seperateThousands(cell.value, 4),
                  accessor: "Count",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  textAlign: "right",
                },
                {
                  Header: t("purchase_price"),
                  accessor: "FifoOpenCost",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (
                    cell: Cell<ProfitsAndLossesTypes.ProfitsAndLosses, number>
                  ) => asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: asEur(fifoOpenCostSum, 2),
                },
                {
                  Header: t("sell_price"),
                  accessor: "DirtyAmount",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (
                    cell: Cell<ProfitsAndLossesTypes.ProfitsAndLosses, number>
                  ) => asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: asEur(dirtyAmountSum, 2),
                },
                {
                  Header: t("profit_loss"),
                  accessor: "ProfitLoss",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (
                    cell: Cell<ProfitsAndLossesTypes.ProfitsAndLosses, number>
                  ) => asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: asEur(profitLossSum, 2),
                },
                {
                  Header: t("percent"),
                  accessor: "Percent",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (
                    cell: Cell<ProfitsAndLossesTypes.ProfitsAndLosses, number>
                  ) => asPercent(cell.value, 2),
                  textAlign: "right",
                  Footer: asPercent(percentSum),
                },
              ];

              const tableData = data.ProfitsAndLosses.map((instrumentData) => ({
                ...instrumentData,
                TransactionDate: dateToString(instrumentData.TransactionDate),
                ClearingDate: dateToString(instrumentData.ClearingDate),
                Percent: countPercent(
                  instrumentData.ProfitLoss,
                  instrumentData.FifoOpenCost
                ),
              }));

              const excelHeaders = [
                tableColumns.map((column) => column.Header),
              ];

              const excelData = data.ProfitsAndLosses.map((instrumentData) => ({
                InstrumentName: instrumentData.InstrumentName,
                TransactionDate: dateToString(instrumentData.TransactionDate),
                ClearingDate: dateToString(instrumentData.ClearingDate),
                Count: instrumentData.Count,
                FifoOpenCost: instrumentData.FifoOpenCost,
                DirtyAmount: instrumentData.DirtyAmount,
                ProfitLoss: instrumentData.ProfitLoss,
                Percent: countPercent(
                  instrumentData.ProfitLoss,
                  instrumentData.FifoOpenCost
                ),
              }));

              const excelSums = {
                InstrumentName: "",
                TransactionDate: "",
                ClearingDate: "",
                Count: 0,
                FifoOpenCost: fifoOpenCostSum,
                DirtyAmount: dirtyAmountSum,
                ProfitLoss: profitLossSum,
                Percent: percentSum,
              };
              excelData.push(excelSums);

              return (
                <Grid
                  key={data.PortfolioId}
                  item
                  xs={12}
                  justifyContent="center"
                  style={{ borderBottom: "1px solid #D3741C" }}
                >
                  <Accordion
                    defaultExpanded={isExpandedDefault}
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      style={{ padding: 0 }}
                    >
                      <PortfolioName name={data.PortfolioName} />
                    </AccordionSummary>
                    <ExportExcel
                      data={excelData}
                      headers={excelHeaders}
                      filename={generateFilename(
                        t("profits_and_losses"),
                        data.PortfolioName,
                        startDate,
                        endDate
                      )}
                      matchesMediaQuery={matchesMediaQuery}
                    />
                    <AccordionDetails
                      style={{
                        maxWidth: "inherit",
                        overflowY: "hidden",
                        padding: matchesMediaQuery
                          ? "8px 16px 16px"
                          : "8px 0 16px 0",
                      }}
                    >
                      <Table
                        columns={tableColumns}
                        data={tableData}
                        loading={profitsAndLossesLoading}
                        hasFooter
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            }
          )
        ) : (
          <NoTransactions />
        )}
      </Grid>
    </Grid>
  );
};

export default React.memo(ProfitsAndLosses);
